/**
 * `max_shift_length` and `night_work_shift_limit`: the shift itself is too
 * long, so giving it to someone else does not help. The guard keeps as much as
 * the limit allows and the rest is handed over.
 */

import { findViolationShift, numberDetail, snapDown, snapUp } from '../rosterModel';
import type { ProposedFix, RemediationStrategy, StrategyResult } from '../types';
import { releaseShiftProposals } from './fixBuilders';

export const shiftLengthStrategy: RemediationStrategy = {
  ruleCodes: ['max_shift_length', 'night_work_shift_limit'],

  async propose(violation, context): Promise<StrategyResult> {
    const shift = findViolationShift(violation, context.snapshot);
    if (!shift) return { proposals: [], unsupportedReason: 'shift_not_on_roster' };

    const limit = numberDetail(violation, 'limit_minutes')
      ?? (violation.rule_code === 'max_shift_length' ? context.rule?.maxShiftMinutes : context.rule?.maxNightShiftMinutes)
      ?? null;
    if (limit === null) return { proposals: [], unsupportedReason: 'no_verified_fix' };

    const proposals: ProposedFix[] = [
      // Usual handover: the guard works the first part, a colleague takes over.
      ...await releaseShiftProposals(shift, context, {
        side: 'end',
        boundaryMinute: snapDown(shift.startMinute + limit),
        handoverLimit: 3,
        preference: 0,
      }),
      // Or the other way round, for when the early hours are the hard ones to fill.
      ...await releaseShiftProposals(shift, context, {
        side: 'start',
        boundaryMinute: snapUp(shift.endMinute - limit),
        handoverLimit: 1,
        preference: 10,
      }),
    ];

    return proposals.length > 0 ? { proposals } : { proposals, unsupportedReason: 'no_eligible_guards' };
  },
};
